import type { ConditionModel } from '@ctw/shared/api/fhir/models/condition';
import { PatientConditionOverviewCard } from '@ctw/shared/content/conditions/patient-conditions-overview-card';
import { usePatientConditionsOverviewData } from '@ctw/shared/content/conditions/use-patient-conditions-overview-data';
import { withOverviewCardErrorBoundary } from '@ctw/shared/content/overview/with-overview-card-error-boundary';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import type { SpecificResourceOverviewCardProps } from '../overview/resource-overview-card';

export interface PatientConditionOverviewProps extends SpecificResourceOverviewCardProps {
  onAfterDetailsDrawerClosed?: () => void;
  conditionIdForDetailsDrawer?: string;
  onOpenDetailsDrawer?: (condition: ConditionModel) => void;
}

export const PatientConditionOverview = withOverviewCardErrorBoundary({
  cardTitle: 'Conditions',
  boundaryName: 'PatientConditionOverview',
  Component: ({
    onSeeAllResources,
    onAfterDetailsDrawerClosed,
    onOpenDetailsDrawer,
  }: PatientConditionOverviewProps) => {
    const { openResourceDrawer } = useResourceDrawer({
      enableReadActions: false,
      onDrawerEvent: (eventType) => {
        if (eventType === 'afterClose') {
          onAfterDetailsDrawerClosed?.();
        }
      },
    });

    const { data, isLoading, hasNoRecords } = usePatientConditionsOverviewData();

    return (
      <PatientConditionOverviewCard
        data={data}
        isLoading={isLoading}
        onRowClick={({ telemetry, record }) => {
          onOpenDetailsDrawer?.(record);
          openResourceDrawer({ telemetry, model: record });
        }}
        emptyState={
          hasNoRecords
            ? "We didn't find any condition records for this patient."
            : 'There are no chronic conditions or acute HCCs for this patient.'
        }
        footerCTA={
          hasNoRecords
            ? undefined
            : {
                label: 'All Conditions',
                onClick: onSeeAllResources,
              }
        }
      />
    );
  },
});
